import { allPieceTypes, directions } from "../const";
import { Move } from "../Move";
import {
  IPiece,
  PieceMovement,
  Position,
  PieceType,
  ISquare,
  IMove,
  MoveType,
  Effect,
} from "../types";
import { BaseMovement } from "./BaseMovement";
import { GameStateManager } from "../Managers/GameStateManager";

export class MonkeyMovement extends BaseMovement implements PieceMovement {
  private readonly capturablePieceTypes: PieceType[] = allPieceTypes;

  getValidMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    isLightTurn?: boolean,
    gameStateManager?: GameStateManager
  ): IMove[] {
    const possibleMoves: IMove[] = [];
    // ZOMBIFICATION: Zombified pieces act as the opposite color
    const effectiveIsLight = piece.zombifiedStatus.isZombified
      ? !piece.isLight
      : piece.isLight;

    // Regular movement and capture
    const regularMoves = this.getValidRegularMoves(
      piece,
      position,
      boardState,
      effectiveIsLight,
      gameStateManager
    );
    possibleMoves.push(...regularMoves);

    // Jumping movement
    const jumpMoves = this.getValidJumpMoves(
      piece,
      position,
      boardState,
      effectiveIsLight,
      gameStateManager
    );
    possibleMoves.push(...jumpMoves);

    // Interaction with Tree piece
    const treeMoves = this.getValidTreeMoves(
      piece,
      position,
      boardState,
      effectiveIsLight
    );
    possibleMoves.push(...treeMoves);

    return possibleMoves;
  }

  private getValidRegularMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    effectiveIsLight: boolean,
    gameStateManager?: GameStateManager
  ): IMove[] {
    const possibleMoves: IMove[] = [];

    for (const direction of directions) {
      const destinationPosition = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };

      if (!this.onBoard(boardState, destinationPosition)) {
        continue;
      }

      const destinationSquare =
        boardState[destinationPosition.row][destinationPosition.col];

      // Check if the target square has multiple pieces (potentially a tree housing another piece)
      if (destinationSquare.Pieces.length > 1) {
        // Check if one of the pieces is a tree
        const hasTree = destinationSquare.Pieces.some(
          (p) => p.type === PieceType.Tree
        );

        // Debug log for tree with other pieces
        if (hasTree) {

          // Log all pieces in the square
          destinationSquare.Pieces.forEach((p, idx) => {
          });

          // Find the non-tree piece in the square
          const housedPiece = destinationSquare.Pieces.find(
            (p) => p.type !== PieceType.Tree
          );

          if (housedPiece) {

            // Allow consume of any Fungi (special monkey consume ability)
            if (housedPiece.type === PieceType.Fungi) {
              const move = new Move(
                piece,
                destinationPosition,
                MoveType.consume,
                housedPiece
              );
              // CRITICAL: Add bonus turn side effect for consume moves so AI can detect them
              move.addSideEffect(destinationPosition, [piece], Effect.BonusTurn());
              possibleMoves.push(move);
              continue;
            } else if (housedPiece.isLight === effectiveIsLight) {
              // If the housed piece is friendly (non-Fungi), skip this move
              continue;
            } else {
              // If the housed piece is an enemy, create a capture move
              possibleMoves.push(
                new Move(
                  piece,
                  destinationPosition,
                  MoveType.capture,
                  housedPiece
                )
              );
              continue;
            }
          }
        }
      }

      const squareInfo = this.getSquareInfo(
        destinationSquare,
        effectiveIsLight
      );

      if (squareInfo.basicSquareInfo) {
        // Special case for Fungi pieces - monkeys can consume any fungi
        if (
          squareInfo.basicSquareInfo.piece &&
          squareInfo.basicSquareInfo.piece.type === PieceType.Fungi
        ) {
          const move = new Move(
            piece,
            destinationPosition,
            MoveType.consume,
            squareInfo.basicSquareInfo.piece
          );
          // CRITICAL: Add bonus turn side effect for consume moves so AI can detect them
          move.addSideEffect(destinationPosition, [piece], Effect.BonusTurn());
          possibleMoves.push(move);
        } else {
          const move = this.getMove(
            position,
            destinationPosition,
            squareInfo.basicSquareInfo,
            piece,
            this.capturablePieceTypes
          );

          if (move) {
            possibleMoves.push(move);
          }
        }
      }
    }

    return possibleMoves;
  }

  private getValidJumpMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    effectiveIsLight: boolean,
    gameStateManager?: GameStateManager
  ): IMove[] {
    const possibleMoves: IMove[] = [];

    // ========== SINGLE JUMP ==========
    for (const direction of directions) {
      const piecePosition = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };
      const endPosition = {
        col: position.col + direction[0] * 2,
        row: position.row + direction[1] * 2,
      };

      if (
        this.onBoard(boardState, piecePosition) &&
        this.onBoard(boardState, endPosition) &&
        this.isValidJumpPiece(
          boardState[piecePosition.row][piecePosition.col],
          effectiveIsLight
        )
      ) {
        const endSquare = boardState[endPosition.row][endPosition.col];

        // Check if the destination contains a tree with a housed piece
        const treePiece = endSquare.Pieces.find(
          (p) => p.type === PieceType.Tree
        );
        if (treePiece && endSquare.Pieces.length > 1) {
          // Find the housed piece
          const housedPiece = endSquare.Pieces.find(
            (p) => p.type !== PieceType.Tree
          );
          if (
            housedPiece &&
            housedPiece.isLight === effectiveIsLight &&
            housedPiece.type !== PieceType.Fungi
          ) {
            // Skip if there's a friendly non-fungi piece in the tree
            continue;
          }
        }

        const endSquareInfo = this.getSquareInfo(endSquare, effectiveIsLight);

        if (endSquareInfo.basicSquareInfo) {
          const occupantPiece = endSquareInfo.basicSquareInfo.piece || null;

          // Handle landing on a tree during a leap
          if (occupantPiece && occupantPiece.type === PieceType.Tree) {
            // CRITICAL FIX: Empty tree after leap should be CAPTURED, not housed
            if (endSquare.Pieces.length === 1) {
              // Empty tree - capture it with a leap move
              const moveType = this.isSwingMove(position, endPosition, boardState)
                ? MoveType.swing
                : MoveType.leap;

              // CRITICAL: Block immediate return swings during bonus turns
              if (moveType === MoveType.swing &&
                  this.isBlockedReturnSwing(position, endPosition, piece, gameStateManager)) {
                continue; // Skip this blocked return swing
              }

              const move = new Move(piece, endPosition, moveType, occupantPiece);

              // CRITICAL: Add bonus turn side effect for swing moves so AI can detect them
              if (moveType === MoveType.swing) {
                move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
              }

              possibleMoves.push(move);
            }
            // Tree with housed piece - only house if the housed piece is capturable
            else if (endSquare.Pieces.length > 1 &&
                endSquare.Pieces.some(
                  (p) =>
                    p.type !== PieceType.Tree &&
                    (p.isLight !== effectiveIsLight ||
                      p.type === PieceType.Fungi)
                )) {
              possibleMoves.push(
                new Move(piece, endPosition, MoveType.house, occupantPiece)
              );
            }
          }
          // Regular leap landing - can also capture friendly Fungi
          else if (
            !occupantPiece ||
            occupantPiece.isLight !== effectiveIsLight ||
            occupantPiece.type === PieceType.Fungi
          ) {
            // Check if consuming any fungi
            if (
              occupantPiece &&
              occupantPiece.type === PieceType.Fungi
            ) {
              // FIX: Use MoveType.leap so danger roll is applied when jumping over enemies
              // The bonus turn is still granted via side effect, processed by BonusTurnHandler
              const move = new Move(
                piece,
                endPosition,
                MoveType.leap,
                occupantPiece
              );
              // CRITICAL: Add bonus turn side effect for leap-consume moves so AI can detect them
              move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
              possibleMoves.push(move);
            }
            // Check if we're trying to land on a Rhino from the front
            else if (
              occupantPiece &&
              occupantPiece.type === PieceType.Rhino &&
              occupantPiece.isLight !== effectiveIsLight
            ) {
              // Determine if this is a frontal attack on the Rhino
              const forwardDirection = effectiveIsLight ? 1 : -1;
              const isAttackingFromFront =
                (effectiveIsLight && endPosition.row > position.row) ||
                (!effectiveIsLight && endPosition.row < position.row);

              // Only allow leap capture if not attacking from the front
              if (!isAttackingFromFront) {
                // Check if this should be a swing move (jumped over tree)
                const moveType = this.isSwingMove(position, endPosition, boardState)
                  ? MoveType.swing
                  : MoveType.leap;

                // CRITICAL: Block immediate return swings during bonus turns
                if (moveType === MoveType.swing &&
                    this.isBlockedReturnSwing(position, endPosition, piece, gameStateManager)) {
                  continue; // Skip this blocked return swing
                }

                const move = new Move(piece, endPosition, moveType, occupantPiece);

                // CRITICAL: Add bonus turn side effect for swing moves so AI can detect them
                if (moveType === MoveType.swing) {
                  move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
                }

                possibleMoves.push(move);
              }
            } else {
              // Regular leap capture for non-Rhino pieces
              // If we're capturing any fungi, use leap type (so danger roll applies)
              if (
                occupantPiece &&
                occupantPiece.type === PieceType.Fungi
              ) {
                // FIX: Use MoveType.leap so danger roll is applied when jumping over enemies
                // The bonus turn is still granted via side effect, processed by BonusTurnHandler
                const move = new Move(
                  piece,
                  endPosition,
                  MoveType.leap,
                  occupantPiece
                );
                // CRITICAL: Add bonus turn side effect for leap-consume moves so AI can detect them
                move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
                possibleMoves.push(move);
              } else {
                // Check if this should be a swing move (jumped over tree)
                const moveType = this.isSwingMove(position, endPosition, boardState)
                  ? MoveType.swing
                  : MoveType.leap;

                // CRITICAL: Block immediate return swings during bonus turns
                if (moveType === MoveType.swing &&
                    this.isBlockedReturnSwing(position, endPosition, piece, gameStateManager)) {
                  continue; // Skip this blocked return swing
                }

                const move = new Move(piece, endPosition, moveType, occupantPiece);

                // CRITICAL: Add bonus turn side effect for swing moves so AI can detect them
                if (moveType === MoveType.swing) {
                  move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
                }

                possibleMoves.push(move);
              }
            }
          }
        }
      }
    }

    // ========== DOUBLE JUMP ==========
    for (const direction of directions) {
      const piecePosition1 = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };
      const emptyPosition = {
        col: position.col + direction[0] * 2,
        row: position.row + direction[1] * 2,
      };
      const piecePosition2 = {
        col: position.col + direction[0] * 3,
        row: position.row + direction[1] * 3,
      };
      const endPosition = {
        col: position.col + direction[0] * 4,
        row: position.row + direction[1] * 4,
      };

      if (
        this.onBoard(boardState, piecePosition1) &&
        this.onBoard(boardState, emptyPosition) &&
        this.onBoard(boardState, piecePosition2) &&
        this.onBoard(boardState, endPosition) &&
        this.isValidJumpPiece(
          boardState[piecePosition1.row][piecePosition1.col],
          effectiveIsLight
        ) &&
        this.isEmptySquare(boardState[emptyPosition.row][emptyPosition.col]) &&
        this.isValidJumpPiece(
          boardState[piecePosition2.row][piecePosition2.col],
          effectiveIsLight
        )
      ) {
        const endSquare = boardState[endPosition.row][endPosition.col];

        // Check if the destination contains a tree with a housed piece
        const treePiece = endSquare.Pieces.find(
          (p) => p.type === PieceType.Tree
        );
        if (treePiece && endSquare.Pieces.length > 1) {
          // Find the housed piece
          const housedPiece = endSquare.Pieces.find(
            (p) => p.type !== PieceType.Tree
          );
          if (
            housedPiece &&
            housedPiece.isLight === effectiveIsLight &&
            housedPiece.type !== PieceType.Fungi
          ) {
            // Skip if there's a friendly non-fungi piece in the tree
            continue;
          }
        }

        const endSquareInfo = this.getSquareInfo(endSquare, effectiveIsLight);

        if (endSquareInfo.basicSquareInfo) {
          const occupantPiece = endSquareInfo.basicSquareInfo.piece || null;

          // Handle landing on a tree during a leap
          if (occupantPiece && occupantPiece.type === PieceType.Tree) {
            // CRITICAL FIX: Empty tree after leap should be CAPTURED, not housed
            if (endSquare.Pieces.length === 1) {
              // Empty tree - capture it with a leap move
              const moveType = this.isSwingMove(position, endPosition, boardState)
                ? MoveType.swing
                : MoveType.leap;

              // CRITICAL: Block immediate return swings during bonus turns
              if (moveType === MoveType.swing &&
                  this.isBlockedReturnSwing(position, endPosition, piece, gameStateManager)) {
                continue; // Skip this blocked return swing
              }

              const move = new Move(piece, endPosition, moveType, occupantPiece);

              // CRITICAL: Add bonus turn side effect for swing moves so AI can detect them
              if (moveType === MoveType.swing) {
                move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
              }

              possibleMoves.push(move);
            }
            // Tree with housed piece - only house if the housed piece is capturable
            else if (endSquare.Pieces.length > 1 &&
                endSquare.Pieces.some(
                  (p) =>
                    p.type !== PieceType.Tree &&
                    (p.isLight !== effectiveIsLight ||
                      p.type === PieceType.Fungi)
                )) {
              possibleMoves.push(
                new Move(piece, endPosition, MoveType.house, occupantPiece)
              );
            }
          }
          // Regular leap landing - can also capture friendly Fungi
          else if (
            !occupantPiece ||
            occupantPiece.isLight !== effectiveIsLight ||
            occupantPiece.type === PieceType.Fungi
          ) {
            // Check if consuming any fungi
            if (
              occupantPiece &&
              occupantPiece.type === PieceType.Fungi
            ) {
              // FIX: Use MoveType.leap so danger roll is applied when jumping over enemies
              // The bonus turn is still granted via side effect, processed by BonusTurnHandler
              const move = new Move(
                piece,
                endPosition,
                MoveType.leap,
                occupantPiece
              );
              // CRITICAL: Add bonus turn side effect for leap-consume moves so AI can detect them
              move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
              possibleMoves.push(move);
            }
            // Check if we're trying to land on a Rhino from the front
            else if (
              occupantPiece &&
              occupantPiece.type === PieceType.Rhino &&
              occupantPiece.isLight !== effectiveIsLight
            ) {
              // Determine if this is a frontal attack on the Rhino
              const forwardDirection = effectiveIsLight ? 1 : -1;
              const isAttackingFromFront =
                (effectiveIsLight && endPosition.row > position.row) ||
                (!effectiveIsLight && endPosition.row < position.row);

              // Only allow leap capture if not attacking from the front
              if (!isAttackingFromFront) {
                // Check if this should be a swing move (jumped over tree)
                const moveType = this.isSwingMove(position, endPosition, boardState)
                  ? MoveType.swing
                  : MoveType.leap;

                // CRITICAL: Block immediate return swings during bonus turns
                if (moveType === MoveType.swing &&
                    this.isBlockedReturnSwing(position, endPosition, piece, gameStateManager)) {
                  continue; // Skip this blocked return swing
                }

                const move = new Move(piece, endPosition, moveType, occupantPiece);

                // CRITICAL: Add bonus turn side effect for swing moves so AI can detect them
                if (moveType === MoveType.swing) {
                  move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
                }

                possibleMoves.push(move);
              }
            } else {
              // Regular leap capture for non-Rhino pieces
              // If we're capturing any fungi, use leap type (so danger roll applies)
              if (
                occupantPiece &&
                occupantPiece.type === PieceType.Fungi
              ) {
                // FIX: Use MoveType.leap so danger roll is applied when jumping over enemies
                // The bonus turn is still granted via side effect, processed by BonusTurnHandler
                const move = new Move(
                  piece,
                  endPosition,
                  MoveType.leap,
                  occupantPiece
                );
                // CRITICAL: Add bonus turn side effect for leap-consume moves so AI can detect them
                move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
                possibleMoves.push(move);
              } else {
                // Check if this should be a swing move (jumped over tree)
                const moveType = this.isSwingMove(position, endPosition, boardState)
                  ? MoveType.swing
                  : MoveType.leap;

                // CRITICAL: Block immediate return swings during bonus turns
                if (moveType === MoveType.swing &&
                    this.isBlockedReturnSwing(position, endPosition, piece, gameStateManager)) {
                  continue; // Skip this blocked return swing
                }

                const move = new Move(piece, endPosition, moveType, occupantPiece);

                // CRITICAL: Add bonus turn side effect for swing moves so AI can detect them
                if (moveType === MoveType.swing) {
                  move.addSideEffect(endPosition, [piece], Effect.BonusTurn());
                }

                possibleMoves.push(move);
              }
            }
          }
        }
      }
    }

    // ========== TRIPLE JUMP ==========
    for (const direction of directions) {
      // Jump over three pieces with empty spaces between them
      // This creates a pattern of: [piece]-[empty]-[piece]-[empty]-[piece]-[landing]
      const firstJumpedPiecePos = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };
      const firstEmptyPos = {
        col: position.col + direction[0] * 2,
        row: position.row + direction[1] * 2,
      };
      const secondJumpedPiecePos = {
        col: position.col + direction[0] * 3,
        row: position.row + direction[1] * 3,
      };
      const secondEmptyPos = {
        col: position.col + direction[0] * 4,
        row: position.row + direction[1] * 4,
      };
      const thirdJumpedPiecePos = {
        col: position.col + direction[0] * 5,
        row: position.row + direction[1] * 5,
      };
      const landingPos = {
        col: position.col + direction[0] * 6,
        row: position.row + direction[1] * 6,
      };

      // Check all positions are on the board
      if (
        !this.onBoard(boardState, firstJumpedPiecePos) ||
        !this.onBoard(boardState, firstEmptyPos) ||
        !this.onBoard(boardState, secondJumpedPiecePos) ||
        !this.onBoard(boardState, secondEmptyPos) ||
        !this.onBoard(boardState, thirdJumpedPiecePos) ||
        !this.onBoard(boardState, landingPos)
      ) {
        continue; // Skip if any position is off the board
      }

      // Get the squares at each position
      const firstJumpedPieceSquare =
        boardState[firstJumpedPiecePos.row][firstJumpedPiecePos.col];
      const firstEmptySquare = boardState[firstEmptyPos.row][firstEmptyPos.col];
      const secondJumpedPieceSquare =
        boardState[secondJumpedPiecePos.row][secondJumpedPiecePos.col];
      const secondEmptySquare =
        boardState[secondEmptyPos.row][secondEmptyPos.col];
      const thirdJumpedPieceSquare =
        boardState[thirdJumpedPiecePos.row][thirdJumpedPiecePos.col];
      const landingSquare = boardState[landingPos.row][landingPos.col];

      // Check if the jumped pieces are valid for jumping over
      if (
        !this.isValidJumpPiece(firstJumpedPieceSquare, effectiveIsLight) ||
        !this.isEmptySquare(firstEmptySquare) ||
        !this.isValidJumpPiece(secondJumpedPieceSquare, effectiveIsLight) ||
        !this.isEmptySquare(secondEmptySquare) ||
        !this.isValidJumpPiece(thirdJumpedPieceSquare, effectiveIsLight)
      ) {
        continue; // Skip if any piece is invalid for jumping over
      }

      // Check if the landing square has a tree with a friendly piece
      const treePiece = landingSquare.Pieces.find(
        (p) => p.type === PieceType.Tree
      );
      if (treePiece && landingSquare.Pieces.length > 1) {
        // Find any non-tree piece on the landing square
        const housedPiece = landingSquare.Pieces.find(
          (p) => p.type !== PieceType.Tree
        );
        if (
          housedPiece &&
          housedPiece.isLight === effectiveIsLight &&
          housedPiece.type !== PieceType.Fungi
        ) {
          // Skip if there's a friendly non-fungi piece in the tree
          continue;
        }
      }

      // Get info about the landing square
      const landingSquareInfo = this.getSquareInfo(
        landingSquare,
        effectiveIsLight
      );
      if (!landingSquareInfo.basicSquareInfo) continue;

      const landingPiece = landingSquareInfo.basicSquareInfo.piece;

      // Handle landing on a tree during a leap (triple jump)
      if (landingPiece && landingPiece.type === PieceType.Tree) {
        // CRITICAL FIX: Empty tree after leap should be CAPTURED, not housed
        if (landingSquare.Pieces.length === 1) {
          // Empty tree - capture it with a leap move
          const moveType = this.isSwingMove(position, landingPos, boardState)
            ? MoveType.swing
            : MoveType.leap;

          // CRITICAL: Block immediate return swings during bonus turns
          if (moveType === MoveType.swing &&
              this.isBlockedReturnSwing(position, landingPos, piece, gameStateManager)) {
            continue; // Skip this blocked return swing
          }

          const move = new Move(piece, landingPos, moveType, landingPiece);

          // CRITICAL: Add bonus turn side effect for swing moves so AI can detect them
          if (moveType === MoveType.swing) {
            move.addSideEffect(landingPos, [piece], Effect.BonusTurn());
          }

          possibleMoves.push(move);
        }
        // Tree with housed piece - only house if the housed piece is capturable
        else if (landingSquare.Pieces.length > 1 &&
            landingSquare.Pieces.some(
              (p) =>
                p.type !== PieceType.Tree &&
                (p.isLight !== effectiveIsLight || p.type === PieceType.Fungi)
            )) {
          possibleMoves.push(
            new Move(piece, landingPos, MoveType.house, landingPiece)
          );
        }
      }
      // Handle landing on empty square or enemy piece or friendly Fungi
      else if (
        !landingPiece ||
        landingPiece.isLight !== effectiveIsLight ||
        landingPiece.type === PieceType.Fungi
      ) {
        // Check if consuming any fungi
        if (
          landingPiece &&
          landingPiece.type === PieceType.Fungi
        ) {
          // FIX: Use MoveType.leap so danger roll is applied when jumping over enemies
          // The bonus turn is still granted via side effect, processed by BonusTurnHandler
          const move = new Move(
            piece,
            landingPos,
            MoveType.leap,
            landingPiece
          );
          // CRITICAL: Add bonus turn side effect for leap-consume moves so AI can detect them
          move.addSideEffect(landingPos, [piece], Effect.BonusTurn());
          possibleMoves.push(move);
        } else {
          // Handle regular leap capture for non-Rhino pieces or other cases
          // Check if this should be a swing move (jumped over tree)
          const moveType = this.isSwingMove(position, landingPos, boardState)
            ? MoveType.swing
            : MoveType.leap;

          // CRITICAL: Block immediate return swings during bonus turns
          if (moveType === MoveType.swing &&
              this.isBlockedReturnSwing(position, landingPos, piece, gameStateManager)) {
            continue; // Skip this blocked return swing
          }

          possibleMoves.push(
            new Move(piece, landingPos, moveType, landingPiece || null)
          );
        }
      }
    }

    return possibleMoves;
  }

  private isValidJumpPiece(square: ISquare, isLight: boolean): boolean {
    // If we have a tree on this square, it's always valid to jump over
    const hasTrepiece = square.Pieces.some((p) => p.type === PieceType.Tree);
    if (hasTrepiece) {
      return true;
    }

    const squareInfo = this.getSquareInfo(square, isLight);

    if (squareInfo.basicSquareInfo?.piece) {
      const { piece } = squareInfo.basicSquareInfo;
      // Only prevent jumping over Viruses and fainted Goats
      return piece.type !== PieceType.Virus && !this.isFaintedGoat(piece);
    }

    return false;
  }

  private isFaintedGoat(piece: IPiece | null): boolean {
    return piece?.type === PieceType.Goat && piece?.isFainted === true;
  }

  private isEmptySquare(square: ISquare): boolean {
    return square.Pieces.length === 0;
  }

  private getValidTreeMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    effectiveIsLight: boolean
  ): IMove[] {
    const possibleMoves: IMove[] = [];

    // Check nearby squares for trees
    for (const direction of directions) {
      const treePosition = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };

      // Skip if out of bounds
      if (!this.onBoard(boardState, treePosition)) {
        continue;
      }

      const treeSquare = boardState[treePosition.row][treePosition.col];
      const treePiece = treeSquare.Pieces.find(
        (p) => p.type === PieceType.Tree
      );

      // Found a tree
      if (treePiece) {
        // Check if there's a housed piece
        if (treeSquare.Pieces.length > 1) {
          const housedPiece = treeSquare.Pieces.find(
            (p) => p.type !== PieceType.Tree
          );
          if (housedPiece) {
            // If housed piece is enemy or a friendly Fungi (consume ability), create a capture move
            if (
              housedPiece.isLight !== effectiveIsLight ||
              housedPiece.type === PieceType.Fungi
            ) {
              // Create move with consume if it's any fungi
              if (housedPiece.type === PieceType.Fungi) {
                const move = new Move(
                  piece,
                  treePosition,
                  MoveType.consume,
                  housedPiece
                );
                // CRITICAL: Add bonus turn side effect for consume moves so AI can detect them
                move.addSideEffect(treePosition, [piece], Effect.BonusTurn());
                possibleMoves.push(move);
              } else {
                possibleMoves.push(
                  new Move(piece, treePosition, MoveType.capture, housedPiece)
                );
              }
            }
          }
        } else {
          // Empty tree - check if monkey can climb it
        }
      }
    }
    return possibleMoves;
  }

  private getJumpPath(
    start: Position,
    end: Position,
    boardState: ISquare[][]
  ): ISquare[] {
    const path: ISquare[] = [];
    const dx = Math.sign(end.col - start.col);
    const dy = Math.sign(end.row - start.row);
    let { col, row } = start;

    while (col !== end.col || row !== end.row) {
      col += dx;
      row += dy;
      if (
        col < 0 ||
        col >= boardState[0].length ||
        row < 0 ||
        row >= boardState.length
      )
        break;
      path.push(boardState[row][col]);
    }
    return path;
  }

  /**
   * Determines if a jump should be considered a "swing" based on jumping over a tree
   * Returns true if the LAST piece jumped over (before landing) is a Tree
   */
  private isSwingMove(
    fromPosition: Position,
    toPosition: Position,
    boardState: ISquare[][]
  ): boolean {
    const rowDiff = toPosition.row - fromPosition.row;
    const colDiff = toPosition.col - fromPosition.col;
    const rowStep = rowDiff === 0 ? 0 : rowDiff > 0 ? 1 : -1;
    const colStep = colDiff === 0 ? 0 : colDiff > 0 ? 1 : -1;

    let lastJumpedOverPiece: IPiece | null = null;
    let currentRow = fromPosition.row + rowStep;
    let currentCol = fromPosition.col + colStep;

    // Walk through the path, excluding the final landing position
    while (currentRow !== toPosition.row || currentCol !== toPosition.col) {
      const currentSquare = boardState[currentRow][currentCol];
      
      // Store the last piece we jump over (if any)
      if (currentSquare.Pieces.length > 0) {
        lastJumpedOverPiece = currentSquare.Pieces[0];
      }

      currentRow += rowStep;
      currentCol += colStep;
    }

    // Return true if the last piece jumped over was a Tree
    return lastJumpedOverPiece?.type === PieceType.Tree;
  }

  /**
   * Check if a swing move would be an "immediate return swing" (blocked)
   * Only blocks if:
   * 1. We're in a bonus turn
   * 2. This is the same monkey that made the last swing
   * 3. The destination exactly matches the last swing's from-position
   * 4. The current position exactly matches the last swing's to-position
   */
  private isBlockedReturnSwing(
    currentPosition: Position,
    destinationPosition: Position,
    piece: IPiece,
    gameStateManager?: GameStateManager
  ): boolean {
    // Can't block if we don't have gameStateManager
    if (!gameStateManager) {
      return false;
    }

    const gameState = gameStateManager.getGameState();

    // Only check during active bonus turns
    if (!gameState.activeBonusTurnPieceId) {
      return false;
    }

    // Only block the specific monkey that created the swing
    if (piece.id !== gameState.lastSwingMonkeyId) {
      return false;
    }

    // Check if there's a blocked swing path set
    if (!gameState.lastSwingFromPosition || !gameState.lastSwingToPosition) {
      return false;
    }

    // Check if this swing is exactly the reverse of the last swing
    // Current position must match last swing's TO position
    // Destination must match last swing's FROM position
    const isReverseSwing =
      currentPosition.row === gameState.lastSwingToPosition.row &&
      currentPosition.col === gameState.lastSwingToPosition.col &&
      destinationPosition.row === gameState.lastSwingFromPosition.row &&
      destinationPosition.col === gameState.lastSwingFromPosition.col;

    if (isReverseSwing) {
    } else {
    }

    return isReverseSwing;
  }
}
